import { useCallback, useEffect, useRef, useState } from "react";
import { Check, ChevronsUpDown, Loader2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { isNamedLead, leadDisplayLabel } from "@/lib/leadDisplayLabel";
import type { ContactLeadOption } from "@/components/ContactLeadPicker";

interface ContactLeadMultiPickerProps {
  value: string[];
  onChange: (leadIds: string[]) => void;
  disabled?: boolean;
  label?: string;
}

function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(t);
  }, [value, delayMs]);
  return debounced;
}

export function ContactLeadMultiPicker({
  value,
  onChange,
  disabled,
  label = "Shared contacts",
}: ContactLeadMultiPickerProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<ContactLeadOption[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [resolvedLeads, setResolvedLeads] = useState<ContactLeadOption[]>([]);
  const debouncedQuery = useDebouncedValue(query, 300);
  const searchAbortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    let cancelled = false;
    const ids = value;

    (async () => {
      const fetched = await Promise.all(
        ids.map(async (id) => {
          try {
            const response = await fetch(`/api/leads/${id}`);
            if (!response.ok) return null;
            const data = await response.json();
            return (data.lead as ContactLeadOption | undefined) ?? null;
          } catch {
            return null;
          }
        }),
      );

      if (cancelled) return;

      const next = fetched.filter((lead): lead is ContactLeadOption => Boolean(lead?._id));
      setResolvedLeads(next);
    })();

    return () => {
      cancelled = true;
    };
  }, [value.join(",")]);

  useEffect(() => {
    if (!open) return;

    searchAbortRef.current?.abort();
    const ac = new AbortController();
    searchAbortRef.current = ac;

    const run = async () => {
      setIsSearching(true);
      try {
        const params = new URLSearchParams({
          dateRange: "All",
          limit: "25",
          page: "1",
          sortBy: "name",
          sortDir: "asc",
        });
        const q = debouncedQuery.trim();
        if (q) params.set("search", q);

        const response = await fetch(`/api/leads?${params.toString()}`, {
          signal: ac.signal,
        });
        if (!response.ok) {
          setResults([]);
          return;
        }
        const data = await response.json();
        const leads = (data.leads || []) as ContactLeadOption[];
        setResults(leads.filter((lead) => isNamedLead(lead)));
      } catch (e) {
        if ((e as Error).name !== "AbortError") {
          setResults([]);
        }
      } finally {
        if (!ac.signal.aborted) setIsSearching(false);
      }
    };

    void run();
    return () => ac.abort();
  }, [open, debouncedQuery]);

  const toggleLead = useCallback(
    (lead: ContactLeadOption) => {
      const exists = value.includes(lead._id);
      if (exists) {
        onChange(value.filter((id) => id !== lead._id));
        setResolvedLeads((prev) => prev.filter((item) => item._id !== lead._id));
      } else {
        onChange([...value, lead._id]);
        setResolvedLeads((prev) =>
          prev.some((item) => item._id === lead._id) ? prev : [...prev, lead],
        );
      }
    },
    [onChange, value],
  );

  const removeLead = useCallback(
    (leadId: string) => {
      onChange(value.filter((id) => id !== leadId));
      setResolvedLeads((prev) => prev.filter((item) => item._id !== leadId));
    },
    [onChange, value],
  );

  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      {resolvedLeads.length > 0 && (
        <ul className="flex flex-wrap gap-2">
          {resolvedLeads.map((lead) => (
            <li
              key={lead._id}
              className="inline-flex items-center gap-1 rounded-md border bg-muted/40 px-2 py-1 text-sm"
            >
              <span className="max-w-[12rem] truncate">{leadDisplayLabel(lead)}</span>
              <button
                type="button"
                className="rounded p-0.5 hover:bg-muted"
                onClick={() => removeLead(lead._id)}
                disabled={disabled}
                aria-label={`Remove ${leadDisplayLabel(lead)}`}
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </li>
          ))}
        </ul>
      )}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="w-full justify-between font-normal"
            disabled={disabled}
          >
            <span className="truncate">
              {value.length === 0
                ? "Add contacts…"
                : `${value.length} contact${value.length === 1 ? "" : "s"} shared`}
            </span>
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-2" align="start">
          <Input
            placeholder="Search name, email, phone…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            autoFocus
          />
          <div className="mt-2 max-h-60 overflow-y-auto">
            {isSearching ? (
              <div className="flex items-center justify-center py-6 text-muted-foreground">
                <Loader2 className="h-5 w-5 animate-spin" />
              </div>
            ) : results.length === 0 ? (
              <p className="py-4 text-center text-sm text-muted-foreground">
                {debouncedQuery.trim() ? "No contacts found" : "Type to search contacts"}
              </p>
            ) : (
              <ul className="space-y-0.5">
                {results.map((lead) => {
                  const isSelected = value.includes(lead._id);
                  return (
                    <li key={lead._id}>
                      <button
                        type="button"
                        className={cn(
                          "flex w-full items-start gap-2 rounded-sm px-2 py-2 text-left text-sm hover:bg-accent",
                          isSelected && "bg-accent",
                        )}
                        onClick={() => toggleLead(lead)}
                      >
                        <Check
                          className={cn(
                            "mt-0.5 h-4 w-4 shrink-0",
                            isSelected ? "opacity-100" : "opacity-0",
                          )}
                        />
                        <span className="min-w-0 flex-1">
                          <span className="block truncate font-medium">
                            {leadDisplayLabel(lead)}
                          </span>
                          {(lead.email || lead.phoneNumber) && (
                            <span className="block truncate text-xs text-muted-foreground">
                              {[lead.email, lead.phoneNumber].filter(Boolean).join(" · ")}
                            </span>
                          )}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
